/**
 * メール配信（クリエイター → 同意したプレイヤー）の API の型と道（pages/mail/ が使う）。
 *
 * 形は Core の確定した実装（docs/teams/core/mail-broadcast-api.md の末尾「実装で決めた細部」・core/mail-broadcast・2026-09-27）。
 * 道は `/v1/admin/mail/*`（sk_）と、開発者のセッション / kdt_ の `/v1/projects/:id/environments/:env/mail/*` の 2 つ
 * （lib/operator.ts の operatorRoute が 2 行に展開する形。share/stats と同じ）。画面は後者を使う。
 * 古いサーバー（ルートが無い）では ApiError.notAvailable（route_missing）になり、画面は「まだ使えない」を落ち着いて出す。
 *
 * 応答の包み（確定）: `{ sender }`（未保存は `{ sender: null }`）・`{ broadcast }`・`{ broadcasts }`・`{ count }`。
 * 状態（status）だけは包まずに `{ state, reason, suspendedAt, sendingDomain, counts }` をそのまま返す。
 */
import { envPath, type EnvName } from './api';
import type { BroadcastStatus, Every } from './mailLogic';
import type { MailWarmup } from './mailWarmup';

export type { MailWarmup };

export const mailPath = (projectId: string, env: EnvName) => `${envPath(projectId, env)}/mail`;

/** 差出人情報（開発者単位 = すべてのゲームで共通）。フッターに必ず入る（法律の表示義務） */
export interface MailSender {
  legalName: string;
  postalAddress: string;
  contactEmail: string;
  /** サーバーが見た「揃っているか」。画面は自分でも senderMissing で数える（入力中の値で判断するため）ので参考 */
  complete?: boolean;
  updatedAt?: string | null;
}

/** GET …/mail/status（包みなし） */
export interface MailStatus {
  state: 'active' | 'suspended';
  /** 停止の理由（'complaint_rate' / 'bounce_rate'・将来は運営の手動停止 'operator'）。画面は既知のものだけ訳し、他はそのまま出す */
  reason?: string | null;
  suspendedAt?: string | null;
  /** 送信に使うドメイン（`<slug>.<C>`）。差出人は `"<ゲーム名>" <noreply@<これ>>`。送信の口が無いサーバーでは null */
  sendingDomain?: string | null;
  /** 直近 30 日の開発者単位の集計 */
  counts?: { sent: number; bounced: number; complained: number };
  /**
   * 新しい差出人の送り始めの 1 日の上限（Core の mail-hardening・2026-09-27）。期間を過ぎたら null・古いサーバーでは欄が無い。
   * 画面は mailWarmup.readWarmup で読む（欄の欠けた応答を 0 と読まない）
   */
  warmup?: MailWarmup | null;
}

/**
 * 1 回の配信の集計。recipients〜unsubscribed が結果の本体で、failed / canceled / pending は内訳の補足
 * （failed = SES が受け取らなかった・canceled = 取り消し / 自動停止で送らなかった・pending = まだ送っていない = 送信中の残り）
 */
export interface BroadcastCounts {
  recipients: number;
  sent: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  failed: number;
  canceled: number;
  pending: number;
}

/**
 * 送れなかった理由（Core の failureReason）。表に無い値が来ても壊れないよう string も受ける（画面はそのまま出す）
 */
export type FailureReason =
  | 'sender_incomplete'
  | 'balance_due'
  | 'suspended'
  | 'feature_disabled'
  | 'kids_audience'
  | 'provider_error'
  | 'canceled'
  | 'internal_error'
  // ゲームの削除・アカウントの閉鎖で止めた（Core の mailBroadcast/teardown.ts・SEC-R3-01・統合⑰）
  | 'project_deleted'
  | 'account_closed'
  // プロジェクトを別のアカウントに渡した（Core の platform/projectTransferAccept.ts・2026-10-21 core/handoff-1a）
  | 'owner_changed';

export interface Broadcast {
  id: string;
  subject: string;
  text?: string;
  html?: string | null;
  status: BroadcastStatus;
  filters?: { language?: string | null; lastPlayedWithinDays?: number | null } | null;
  /** 予約の時刻・定期の最初の回（UTC） */
  sendAt?: string | null;
  recurrence?: { every: Every; at: string } | null;
  /** 定期の次回（UTC） */
  nextRunAt?: string | null;
  /** 定期の回ごとの行（子）なら親の id と、その回の時刻 */
  parentId?: string | null;
  occurrenceAt?: string | null;
  counts?: Partial<BroadcastCounts> | null;
  createdAt: string;
  /** 送信を始めた時刻（Core は startedAt と同じ値を入れる） */
  sentAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  canceledAt?: string | null;
  /** failed / canceled のときの理由 */
  failureReason?: FailureReason | string | null;
}
